import React from 'react';
import ReactDOM from 'react-dom/client';
import { PostHogProvider } from '@posthog/react';
import App from './App';
import './index.css';
import './i18n/config';
import { I18nBoundary } from './components/I18nBoundary';
import { initializeAnalytics } from './lib/analytics';
import { resetServerSessionValidation } from './lib/auth-session-state';
import {
  enqueuePendingSignupCompletedFromOAuthReturn,
  enqueuePendingSignInFromOAuthReturn,
} from './lib/reddit-pixel';
import { useAuth } from './stores/use-auth';
import { ORG_CHOICE_SESSION_KEY } from './lib/org-choice-session';
import { installStaleChunkReload } from './lib/stale-chunk-reload';

/**
 * OAuth redirect may include ?access_token=... (primary) or legacy #access_token=… when HttpOnly
 * cookies are not sent cross-origin. Query survives 302 Location more reliably than fragment
 * (Safari / some proxies). Must run synchronously before React so the first /users/me sees Bearer.
 * Zustand persist merge (use-auth) must keep this token over rehydrated null — see merge in store.
 */
/** OAuth bootstrap: token / org-choice marker. */
function bootstrapOAuthAccessTokenFromUrl(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  let accessToken = url.searchParams.get('access_token');
  if (!accessToken && url.hash.includes('access_token=')) {
    const params = new URLSearchParams(url.hash.replace(/^#/, ''));
    accessToken = params.get('access_token');
  }
  const needsOrgChoice = url.searchParams.get('needs_org_choice');
  const oauthAccountCreated = url.searchParams.get('oauth_account_created') === '1';

  if (accessToken) {
    resetServerSessionValidation();
    useAuth.getState().setSessionToken(accessToken);
    url.searchParams.delete('access_token');
  }
  if (needsOrgChoice === '1') {
    sessionStorage.setItem(ORG_CHOICE_SESSION_KEY, '1');
    url.searchParams.delete('needs_org_choice');
  }
  if (oauthAccountCreated) {
    url.searchParams.delete('oauth_account_created');
    enqueuePendingSignupCompletedFromOAuthReturn();
  } else if (accessToken) {
    enqueuePendingSignInFromOAuthReturn();
  }
  url.hash = '';

  if (Boolean(accessToken) || needsOrgChoice === '1' || oauthAccountCreated) {
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }
}

installStaleChunkReload();
bootstrapOAuthAccessTokenFromUrl();

const posthogClient = initializeAnalytics();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PostHogProvider client={posthogClient}>
      <I18nBoundary>
        <App />
      </I18nBoundary>
    </PostHogProvider>
  </React.StrictMode>
);
